/* eslint-disable react-hooks/exhaustive-deps */
import { ErrorMessage, Field } from 'formik';
import { Col, Form, FormControl, Row } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import Editor from '../../../../../components/editor/Editor';
import ImageWithErrorHandling from '../../../../../components/image/ImageWithErrorHandling';
import { ImageSelect } from '../../../../../components/inputs/ImageSelect';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useGetAllGalleriesQuery } from '../../../../../services/endpoints/website/gallery';
import { getPartition } from '../../../../../utils/utils';
import { StepProps } from './AddEditPage';

export const PageForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
  const {
    data: galleries,
    error: galleriesError
  } = useGetAllGalleriesQuery();

  const imageRequired = !values?.image_file_name;

  return (
    <>
      <Row>
        <Col xs={12}>
          <FormStepHeader title="Page Details" showRequiredLegend={!readOnly} />
        </Col>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="name"
          >
            <FormLabel required>Name</FormLabel>
            <Field
              name="name"
              as={Form.Control}
              isInvalid={touched.name && !!errors.name}
              aria-required
            />
            <ErrorMessage name="name">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="author"
          >
            <FormLabel required>Author</FormLabel>
            <Field
              name="author"
              as={Form.Control}
              isInvalid={touched.author && !!errors.author}
              aria-required
            />
            <ErrorMessage name="author">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="published_on"
          >
            <FormLabel required>Published On</FormLabel>
            <Field
              name="published_on"
              type="date"
              as={Form.Control}
              isInvalid={touched.published_on && !!errors.published_on}
              aria-required
            />
            <ErrorMessage name="published_on">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="image_name"
          >
            <ImageSelect
              label={`Image${values?.image_file_name ? ` (${values?.image_file_name})` : ''}`}
              name="image"
              onChange={setFieldValue}
              required={imageRequired}
            />
            <ImageWithErrorHandling
              src={`${values?.s3_base_url}/${getPartition(values?.id)}/thumb/${values?.image_file_name}`}
              alt="Error loading image"
            />
            <ErrorMessage name="image">
              {(msg) => (
                <div className="text-danger small mt-1">
                  {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                </div>
              )}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="is_featured"
          >
            <Field
              name="is_featured"
              type="checkbox"
              as={Form.Check}
              label="Is Featured"
            />
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="content"
          >
            <FormLabel required>Content</FormLabel>
            <Field
              name="content"
              component={Editor}
              customItems={[
                'heading',
                'bold',
                'italic',
                'fontColor',
                'bulletedList',
                'imageUpload',
                'insertTable',
                'horizontalLine',
                'link',
                'undo',
                'redo'
              ]}
              isInvalid={touched.content && !!errors.content}
              aria-required
            />
            <ErrorMessage name="content">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="button_text"
          >
            <FormLabel required>Button Text</FormLabel>
            <Field
              name="button_text"
              as={Form.Control}
              isInvalid={touched.button_text && !!errors.button_text}
              aria-required
            />
            <ErrorMessage name="button_text">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="button_target"
          >
            <FormLabel required>Button Target</FormLabel>
            <Field
              name="button_target"
              as={Form.Control}
              isInvalid={touched.button_target && !!errors.button_target}
              aria-required
            />
            <ErrorMessage name="button_target">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="button_opens_in_new_window"
          >
            <Field
              name="button_opens_in_new_window"
              type="checkbox"
              as={Form.Check}
              label="Button Opens In New Window"
            />
            <ErrorMessage name="button_opens_in_new_window">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="gallery_id"
          >
            <Field
              as={FormSelect}
              name="gallery_id"
              label="Gallery"
              required
              options={galleries?.results?.map((g) => ({ value: g.id, label: g.name })) ?? []}
              loadingError={
                galleriesError
                  ? 'Error loading galleries'
                  : touched.gallery_id && !!errors.gallery_id
                    ? 'Gallery selection is required'
                    : undefined
              }
            />
            <ErrorMessage name="gallery_id">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};
